import React, { useEffect, useState } from 'react';
import { LuCircleAlert, LuLink, LuLoaderCircle, LuServer } from 'react-icons/lu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';

const initialForm = (catalog = {}) => ({
  name: '',
  host: '',
  port: 22,
  resource_class: catalog.default_resource_class || catalog.resource_classes?.[0]?.value || 'server',
  region: '',
  os: '',
  external_id: '',
  monthly_cost: '',
  tags: [],
  groups: ['existing-servers'],
});

const AttachServerDialog = ({ open, provider, onClose, onSubmit }) => {
  const catalog = provider?.existing_resources || {};
  const [form, setForm] = useState(initialForm(catalog));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(initialForm(catalog));
      setError('');
    }
  }, [open, provider?.id]);

  const update = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSubmit(provider, {
        ...form,
        port: Number(form.port || 22),
        monthly_cost: Number(form.monthly_cost || 0),
        region: form.region || 'External',
      });
      onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'The server could not be added');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold text-zinc-100">
            <LuServer className="text-amber-400" /> {catalog.label || 'Add existing server'}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-sm text-blue-200">
          <p>{catalog.description || 'Attach a server you already operate to Cloud Watcher inventory.'}</p>
          <p className="mt-1 text-xs text-blue-300/70">This only adds an inventory record. It will not provision, restart, or delete anything at {provider?.name}.</p>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="attach-name">Server name</Label>
              <Input id="attach-name" required value={form.name} onChange={e => update('name', e.target.value)} placeholder="Aaru" className="mt-2 bg-zinc-800 border-zinc-700" />
            </div>
            <div>
              <Label htmlFor="attach-class">Server class</Label>
              <select id="attach-class" value={form.resource_class} onChange={e => update('resource_class', e.target.value)} className="mt-2 h-10 w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 text-sm text-zinc-100">
                {(catalog.resource_classes || []).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-[1fr_96px] gap-4">
            <div>
              <Label htmlFor="attach-host">Public IP or hostname</Label>
              <Input id="attach-host" required value={form.host} onChange={e => update('host', e.target.value)} placeholder="server.example.com" className="mt-2 bg-zinc-800 border-zinc-700 font-mono" />
            </div>
            <div>
              <Label htmlFor="attach-port">SSH port</Label>
              <Input id="attach-port" required type="number" min="1" max="65535" value={form.port} onChange={e => update('port', e.target.value)} className="mt-2 bg-zinc-800 border-zinc-700" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="attach-region">Location</Label>
              <Input id="attach-region" value={form.region} onChange={e => update('region', e.target.value)} placeholder="Germany" className="mt-2 bg-zinc-800 border-zinc-700" />
            </div>
            <div>
              <Label htmlFor="attach-os">Operating system</Label>
              <Input id="attach-os" value={form.os} onChange={e => update('os', e.target.value)} placeholder="Ubuntu 24.04" className="mt-2 bg-zinc-800 border-zinc-700" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="attach-external-id">Provider reference <span className="text-zinc-600">(optional)</span></Label>
              <Input id="attach-external-id" value={form.external_id} onChange={e => update('external_id', e.target.value)} placeholder="Contract or server ID" className="mt-2 bg-zinc-800 border-zinc-700" />
            </div>
            <div>
              <Label htmlFor="attach-cost">Monthly cost (USD)</Label>
              <Input id="attach-cost" type="number" min="0" step="0.01" value={form.monthly_cost} onChange={e => update('monthly_cost', e.target.value)} placeholder="0.00" className="mt-2 bg-zinc-800 border-zinc-700" />
            </div>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-3 text-xs text-zinc-500">
            SSH credentials are deliberately not collected here. After adding the server, open its Terminal tab to scan and verify the host fingerprint, then save a dedicated password or private key securely.
          </div>

          {error && <div role="alert" className="flex items-start gap-2 rounded-lg border border-rose-500/20 bg-rose-500/10 p-3 text-sm text-rose-300"><LuCircleAlert className="mt-0.5 shrink-0" />{error}</div>}

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-amber-400 disabled:opacity-50">
              {saving ? <LuLoaderCircle className="animate-spin" /> : <LuLink />} {saving ? 'Adding…' : 'Add to inventory'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AttachServerDialog;
